/* eslint-disable jsx-a11y/anchor-is-valid */
/* ------------------------------------------------------------------------ */
/*		File: cmpFromApply.tsx                                              */
/*		Description: Component Implementation                               */
/*		Project: iSymWebSchool											    */
/*		Author: ISym Global Solutions Limited                               */
/*		Version: 1.0													    */
/* ------------------------------------------------------------------------ */

import React, { useEffect, useState } from "react";
import { ICard, ICmpApplyForm } from "./cmpFormApply.d";
import "./cmpFormApply.css";
import Button from "../GenericComponents/Button";
import { setLead } from "../../api/hooks";
import { useNavigate } from "react-router-dom";
import Popup from "../../components/cmpPopup/cmpPopup";
import Policy from "../cmpPolicy/cmpPolicy";
import Spinner from "../GenericComponents/spinner";
import { ToastContainer, Zoom, toast } from 'react-toastify';
import { forEachChild } from "typescript";

type FormData = {
    [key: string]: any;
};

/* ------------------------------------------------------------------------ */

const FormApply: React.FC<ICard> = ({ fields, validations, buttons, policy, msgError, campaign }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<FormData>(() => {
        const initialFormData = fields.concat(validations).reduce<FormData>((acc, field) => {
            if (field.type === "checkbox") {
                acc[field.id] = false;
            } else if (field.type === "dropdown" && field.options?.length) {
                acc[field.id] = field.options[0].value;
            } else {
                acc[field.id] = "";
            }
            return acc;
        }, {});

        return {...initialFormData, personalKey: null};
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (campaign && campaign !== null) {
            setFormData({
                ...formData,
                promocode: campaign.personalKey,
                personalKey: campaign.personalKey,
                name: campaign.name || "",
                surname: campaign.surname || "",
                email: campaign.email || "",
            })
        }
        
    }, [campaign])


    const togglePopup = () => { setIsOpen(!isOpen) };

    /* ---------------------------------------- */
    /* Handler Event: handleChange              */
    /* ---------------------------------------- */

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === "checkbox") {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData({
                ...formData,
                [name]: checked
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    /* ---------------------------------------- */
    /* Handler Event: validateField             */
    /* ---------------------------------------- */

    const validateField = (field: ICmpApplyForm): string | null => {
        const value = formData[field.id];

        if (field.validation?.required) {
            if (field.type === "checkbox") {

                if (!value || value === false) {
                    return field.validation.errorMessage || `${field.label} is required.`;
                }
            } else {
                if (!value || value === "") {
                    return field.validation.errorMessage || `${field.label} is required.`;
                }
            }
        }

        if (field.validation?.pattern && value) {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
                return field.validation.errorMessage || `${field.label} is not valid.`;
            }
        }

        return null;
    };

    /* ---------------------------------------- */
    /* Handler Event: handleSubmit              */
    /* ---------------------------------------- */

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        setIsLoading(true);
        e.preventDefault();

        const newErrors: { [key: string]: string } = {};

        fields.forEach(item => {
            const error = validateField(item);
            if (error) { newErrors[item.id] = error; }
        });
        validations.forEach(item => {
            const error = validateField(item);
            if (error) { newErrors[item.id] = error; }
        });

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            

            if (formData.personalKey) {
                formData.promocode = campaign.promocodeId
            }
            if (formData.personalKey === undefined) { formData.personalKey = null }
            if (formData.promocode === undefined) { formData.promocode = null }

            const response: any = await setLead(formData);
            if (response.msg === "OK") {
                navigate(`${buttons[0]?.navigateTo}`)
            } else {
                toast.error(msgError)
            }
        }
        setIsLoading(false);
    };

    /* ---------------------------------------- */
    /* Handler Event: createLabelWithLink       */
    /* ---------------------------------------- */

    const createLabelWithLink = (label: string) => {
        const parts = label.split("<a>");

        return (
            <>
                {parts[0]}
                <a href="#" onClick={togglePopup}> 
                    {parts[1]?.replace("</a>", "")} 
                </a>
            </>
        );
    };
    /* ---------------------------------------- */

    return (
        <div className="card_formApply">
            {isLoading
                ? (<Spinner isLoading={isLoading} type="container"/>)
                : (
                    <form id="dynamic-form" onSubmit={handleSubmit}>
                        {fields.map((item, index) => (
                            <div
                                key={`div-${index}`}
                                className={`card_formApply_container ${item.type?.toLowerCase() === "textarea" ? "card_top" : "card_center"
                                    }`}
                            >
                                <div className="card_formApply_label">
                                    {item.label}
                                    {item.validation?.required && <span>*</span>}
                                </div>
                                <div className="card_formApply_field">
                                    {item.type?.toLowerCase() === "input" && (
                                        <input
                                            id={item.id}
                                            name={item.id}
                                            type="text"
                                            value={formData[item.id] as string}
                                            onChange={handleChange}
                                            disabled={(item.id === "promocode" || item.id === "email") && campaign !== null}
                                            className={`${(item.id === "promocode" || item.id === "email") && campaign !== null ? "disabled_form" : ""}`}
                                        />
                                    )}
                                    {item.type?.toLowerCase() === "textarea" && (
                                        <textarea
                                            id={item.id}
                                            name={item.id}
                                            value={formData[item.id] as string}
                                            onChange={handleChange}
                                            rows={5}
                                        />
                                    )}
                                    {item.type?.toLowerCase() === "dropdown" && (
                                        <select
                                            id={item.id}
                                            name={item.id}
                                            value={formData[item.id] as string}
                                            onChange={handleChange}
                                        >
                                            {item.options?.map((option, i) => (
                                                <option
                                                    key={`option-${option.value}`}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {item.type?.toLowerCase() === "checkbox" && (
                                        <input
                                            id={item.id}
                                            name={item.id}
                                            type="checkbox"
                                            checked={formData[item.id] as boolean}
                                            onChange={handleChange}
                                        />
                                    )}
                                    {errors[item.id] && <span className="error">{errors[item.id]}</span>}
                                </div>
                            </div>
                        ))}

                        {validations.map((item, index) => (
                            <div
                                key={`divVald-${index}`}
                                className="card_formApply_container_validations"
                            >
                                <div className="card_formApply_container_checkbox">
                                    <input
                                        id={item.id}
                                        type="checkbox"
                                        name={item.id}
                                        checked={formData[item.id] as boolean}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="card_formApply_validations_field">
                                    <label
                                        className="card_formApply_container_label"
                                        htmlFor={item.id}
                                    >
                                        {createLabelWithLink(item.label!)}
                                    </label>
                                    {errors[item.id] && <span className="error">{errors[item.id]}</span>}
                                </div>
                            </div>
                        ))}

                        <div className="card_formApply_button">
                            <Button
                                classProp={`${buttons[0].color}-button`}
                                label={buttons[0].label}
                                hasIcon={!!buttons[0].src}
                                src={buttons[0].src}
                                size={"big"}

                            />
                        </div>

                        {isOpen && (
                            <Popup
                                content={
                                    <Policy
                                        card={policy}
                                    />
                                }
                                type="policy"
                                popupOptions={undefined}
                                handleClose={togglePopup}
                            />
                        )}

                    </form>
                )}
            <ToastContainer
                style={{width: "500pt"}}
                position="top-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Zoom}
            />

        </div>
    );
};

export default FormApply;

